import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, linkedSignal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { readonly, schema } from "@angular/forms/signals";
import { ActivatedRoute, Router } from "@angular/router";
import { AccountType, SbCreative, SbCreativeType } from "@front/m19-api-client";
import { sbAsinsToList } from "@front/m19-models";
import { AccountSelectionService, LandingPage, SbStrategyService, TacosStrategiesService } from "@front/m19-services";
import { ToasterService } from "@front/m19-ui";
import { parseIdFromParam } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";

import {
  algoConfigFromStrategy,
  assetsDefaultFormModel,
  creativesSbAsinsDefaultFormModel,
  formatDefaultFormModel,
  setupDefaultFormModel,
} from "@m19-board/strategies/sb-create-strategy/sb-create-strategy-utils";
import { combineLatest, map, Observable, of, switchMap, take } from "rxjs";
import { SbWizardPageComponent } from "../sb-wizard-page/sb-wizard-page.component";
import { SbFormContextWithCreative, SbWizardCreationService } from "../shared/sb-wizard-creation.service";
import { createSbWizardForm } from "../shared/sb-wizard-form";
import {
  SB_STRATEGIES_URL,
  SB_WIZARD_FORM_STEP,
  SbWizardFormModel,
  TACOS_STRATEGIES_URL,
} from "../shared/sb-wizard-types";

@Component({
  selector: "app-sb-creative-edit-page",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SbWizardCreationService],
  imports: [SbWizardPageComponent],
  template: `
    <app-sb-wizard-page
      [form]="editForm"
      [steps]="steps()"
      [ready]="formContext() != null"
      [strategyName]="strategyName() ?? ''"
      [breadcrumbLink]="breadcrumbLink()"
      [tacosSpAsins]="tacosSpAsinList()"
      submitLabelKey="common.save"
      (cancelled)="goBack()"
    />
  `,
})
export class SbCreativeEditPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly translocoService = inject(TranslocoService);
  private readonly toasterService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sbWizardCreationService = inject(SbWizardCreationService);

  private readonly strategyId$ = this.route.paramMap.pipe(map((params) => parseIdFromParam(params.get("strategyId"))));
  private readonly creativeId$ = this.route.paramMap.pipe(map((params) => parseIdFromParam(params.get("creativeId"))));
  private readonly tacosStrategyGroupId$ = this.route.paramMap.pipe(
    map((params) => parseIdFromParam(params.get("tacosId"))),
  );
  private readonly tacosStrategyGroupId = toSignal(this.tacosStrategyGroupId$, { requireSync: true });

  readonly formContext = toSignal(
    combineLatest([this.strategyId$, this.creativeId$]).pipe(
      switchMap(([strategyId, creativeId]) => {
        if (strategyId == null || creativeId == null) return of(null);
        return this.sbWizardCreationService.loadContext({ strategyId, creativeId }).pipe(take(1));
      }),
    ),
    { initialValue: null },
  );

  readonly tacosSpAsinList = toSignal(
    combineLatest([this.tacosStrategyGroupId$, this.accountSelectionService.singleAccountMarketplaceSelection$]).pipe(
      switchMap(([tacosId, am]) => {
        if (tacosId == null) return of(undefined);
        return this.tacosStrategiesService.getTacosSPAsins(am.accountId, am.marketplace, tacosId);
      }),
    ),
  );

  readonly step = toSignal(
    this.route.data.pipe(
      map((data) => {
        return data["step"] === "assets" ? SB_WIZARD_FORM_STEP.assets : SB_WIZARD_FORM_STEP.creativesSbAsins;
      }),
    ),
    {
      requireSync: true,
    },
  );
  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  // TODO: remove after full product collection migration
  readonly isProductCollection = computed(
    () => this.formContext()?.creative.creativeType === SbCreativeType.productCollection,
  );

  readonly steps = computed(() =>
    this.isProductCollection() ? [SB_WIZARD_FORM_STEP.assets, SB_WIZARD_FORM_STEP.creativesSbAsins] : [this.step()],
  );
  readonly isAsinsStep = computed(() => this.step() === SB_WIZARD_FORM_STEP.creativesSbAsins);

  readonly isVendor = computed(() => this.accountMarketplace().accountType === AccountType.VENDOR);
  readonly marketplace = computed(() => this.accountMarketplace().marketplace);
  readonly strategyName = computed(() => this.formContext()?.strategy.name);
  readonly breadcrumbLink = computed(() => {
    const tacosStrategyGroupId = this.tacosStrategyGroupId();
    if (tacosStrategyGroupId != null) {
      return [TACOS_STRATEGIES_URL, tacosStrategyGroupId];
    }
    const strategyId = this.formContext()?.strategy.strategyId;
    if (strategyId != null) return [SB_STRATEGIES_URL, strategyId, "ad-lines"];
    return null;
  });

  private readonly formModel = linkedSignal<SbWizardFormModel>(() => {
    const context = this.formContext();
    if (!context) {
      return {
        setup: setupDefaultFormModel,
        format: formatDefaultFormModel,
        assets: assetsDefaultFormModel,
        creativesSbAsins: creativesSbAsinsDefaultFormModel,
      };
    }

    return {
      setup: {
        algo: algoConfigFromStrategy(context.strategy),
        brand: context.brand,
        name: context.strategy.name,
      },
      format: {
        sbLandingPage: context.creative.storePageId ? LandingPage.Store : LandingPage.ProductListing,
        sbCreativeType: context.creative.creativeType,
        store: context.assets.store ?? null,
      },
      assets: {
        brandName: context.creative.brandName,
        headline: context.creative.headline ?? "",
        brandLogo: context.assets.brandLogo ?? null,
        video: context.assets.video ?? null,
      },
      creativesSbAsins: {
        sbAsins: context.creative.creativeAsins ?? [],
      },
    };
  });

  readonly editForm = createSbWizardForm({
    formModel: this.formModel,
    isVendor: this.isVendor,
    marketplace: this.marketplace,
    submitForm: async () => this.submit(),
    customSchema: (_) => {
      return schema<SbWizardFormModel>((s) => {
        readonly(s.assets.brandName);
      });
    },
  });

  submit() {
    if (!this.editForm().valid()) return;
    if (this.isProductCollection()) {
      this.saveAndMigrateProductCollection();
      return;
    }
    if (this.isAsinsStep()) {
      this.saveAsins();
      return;
    }
    this.saveAssets();
  }

  goBack() {
    const tacosStrategyGroupId = this.tacosStrategyGroupId();
    if (tacosStrategyGroupId != null) {
      void this.router.navigate([TACOS_STRATEGIES_URL, tacosStrategyGroupId]);
      return;
    }
    const strategyId = this.formContext()?.strategy.strategyId;
    if (strategyId) {
      void this.router.navigate([SB_STRATEGIES_URL, strategyId, "ad-lines"]);
    }
  }

  private saveAndMigrateProductCollection() {
    const context = this.formContext();
    if (!context) return;

    this.sbWizardCreationService.isSubmitting.set(true);
    const asinCluster = this.editForm.creativesSbAsins
      .sbAsins()
      .value()
      .filter((group) => sbAsinsToList(group).length > 0);
    const updatedCreative: SbCreative = {
      ...this.buildUpdatedCreative(context),
      creativeAsins: asinCluster,
    };

    this.runUpdate(
      this.sbStrategyService.migrateSbCreativeToManualCollection(updatedCreative),
      "sb-creative-edit.product_collection_migrated",
    );
  }

  private saveAssets() {
    const context = this.formContext();
    if (!context) return;

    this.sbWizardCreationService.isSubmitting.set(true);
    this.runUpdate(
      this.sbStrategyService.updateSbCreative(context.creative, this.buildUpdatedCreative(context)),
      "sb-creative-edit.assets_updated",
    );
  }

  private saveAsins() {
    const context = this.formContext();
    if (!context) return;

    const asinCluster = this.editForm.creativesSbAsins
      .sbAsins()
      .value()
      .filter((group) => sbAsinsToList(group).length > 0);

    this.sbWizardCreationService.isSubmitting.set(true);
    this.runUpdate(
      this.sbStrategyService.updateSbCreativeClusterAsync(context.creative, asinCluster),
      "sb-creative-edit.asins_updated",
    );
  }

  private runUpdate(update$: Observable<unknown>, successKey: string) {
    update$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toasterService.success(this.translocoService.translate(successKey));
        this.goBack();
      },
      error: (err) => {
        this.toasterService.error(err, this.translocoService.translate("sb-creative-edit.update_error"));
        this.sbWizardCreationService.isSubmitting.set(false);
      },
    });
  }

  private buildUpdatedCreative(context: SbFormContextWithCreative): SbCreative {
    const assets = this.editForm.assets().value();
    return {
      ...context.creative,
      brandName: assets.brandName,
      headline: assets.headline?.trim() || "",
      logoAssetId: assets.brandLogo?.assetId,
      videoAssetId: assets.video?.assetId,
    };
  }
}
